// Zone detection — resolves visitor shipping zone for AE Foodstore.
// Priority chain:
//   1. cart.shipping_address.province_code (most accurate)
//   2. localStorage cache (30d TTL)
//   3. Shopify.country check (hide bar if non-US)
//   4. ipapi.co fetch (state + zip from IP)
//   5. Fallback: us_far ($79 conservative)
// Dispatches `ae-zone-ready` event with detail { zone, threshold, label, source }.
// Bot detection skips ipapi to save quota.
// Failure: 3 ipapi fails → 24h cooldown.
(function () {
  'use strict';

  const ZONES = {
    dfw:         { threshold: 5900, label: 'FREE local delivery', sub: '20–30 min delivery' },
    tx_local:    { threshold: 5900, label: 'FREE shipping',       sub: '' },
    us_standard: { threshold: 6900, label: 'FREE shipping',       sub: '' },
    us_far:      { threshold: 7900, label: 'FREE shipping',       sub: '' },
    remote:      { threshold: null, label: 'USPS calculated rate', sub: '' },
  };

  /* TX Local: TX, OK ($59) */
  /* US Standard: 25 states ($69) */
  /* US Far Extended: 22 states ($79) */
  /* Remote: AK, HI, PR (USPS) */
  const STATE_ZONE = {
    'TX': 'tx_local', 'OK': 'tx_local',
    'AR': 'us_standard', 'LA': 'us_standard', 'MS': 'us_standard',
    'AL': 'us_standard', 'TN': 'us_standard', 'KY': 'us_standard',
    'MO': 'us_standard', 'KS': 'us_standard', 'IA': 'us_standard',
    'IL': 'us_standard', 'IN': 'us_standard', 'OH': 'us_standard',
    'GA': 'us_standard', 'FL': 'us_standard', 'SC': 'us_standard',
    'NC': 'us_standard', 'VA': 'us_standard', 'WV': 'us_standard',
    'MD': 'us_standard', 'DE': 'us_standard', 'DC': 'us_standard',
    'PA': 'us_standard', 'NM': 'us_standard', 'NE': 'us_standard',
    'NJ': 'us_standard',
    'CA': 'us_far', 'OR': 'us_far', 'WA': 'us_far',
    'NV': 'us_far', 'ID': 'us_far', 'UT': 'us_far',
    'AZ': 'us_far', 'CO': 'us_far', 'WY': 'us_far',
    'MT': 'us_far', 'ND': 'us_far', 'SD': 'us_far',
    'MN': 'us_far', 'WI': 'us_far', 'MI': 'us_far',
    'NY': 'us_far', 'CT': 'us_far', 'RI': 'us_far',
    'MA': 'us_far', 'VT': 'us_far', 'NH': 'us_far',
    'ME': 'us_far',
    'AK': 'remote', 'HI': 'remote', 'PR': 'remote'
  };

  const FALLBACK_ZONE = 'us_far';
  const CACHE_KEY = 'ae_zone_v1';
  const CACHE_TTL = 30 * 24 * 60 * 60 * 1000;
  const FAIL_KEY = 'ae_zone_fail_v1';
  const FAIL_LIMIT = 3;
  const FAIL_COOLDOWN = 24 * 60 * 60 * 1000;

  function isBot() {
    if (navigator.webdriver) return true;
    return /bot|crawl|spider|headless|googlebot|bingbot|slurp|duckduckbot|baiduspider|yandex/i.test(navigator.userAgent || '');
  }

  function isDFW(state, postal) {
    if (state !== 'TX' || !postal) return false;
    const z = parseInt(String(postal).slice(0, 5), 10);
    return z >= 75001 && z <= 76299;
  }

  function resolveZoneKey(state, postal) {
    if (isDFW(state, postal)) return 'dfw';
    return STATE_ZONE[state] || FALLBACK_ZONE;
  }

  function dispatch(zoneKey, source, detail) {
    const z = ZONES[zoneKey] || ZONES[FALLBACK_ZONE];
    const payload = Object.assign({}, z, { zone: zoneKey, source: source }, detail || {});
    window.AE_ZONE = payload;
    document.dispatchEvent(new CustomEvent('ae-zone-ready', { detail: payload }));
  }

  function loadCache() {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const d = JSON.parse(raw);
      if (Date.now() - d.t > CACHE_TTL) return null;
      return d;
    } catch (e) { return null; }
  }
  function saveCache(state, postal) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), state: state, postal: postal || '' }));
    } catch (e) {}
  }
  function isFailCooled() {
    try {
      const raw = localStorage.getItem(FAIL_KEY);
      if (!raw) return false;
      const d = JSON.parse(raw);
      if (d.count >= FAIL_LIMIT && Date.now() - d.t < FAIL_COOLDOWN) return true;
      return false;
    } catch (e) { return false; }
  }
  function recordFail() {
    try {
      const raw = localStorage.getItem(FAIL_KEY);
      const d = raw ? JSON.parse(raw) : { count: 0, t: Date.now() };
      d.count = (d.count || 0) + 1;
      if (d.count === 1) d.t = Date.now();
      localStorage.setItem(FAIL_KEY, JSON.stringify(d));
    } catch (e) {}
  }
  function clearFail() {
    try { localStorage.removeItem(FAIL_KEY); } catch (e) {}
  }

  async function detectFromCart() {
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await r.json();
      const addr = cart.shipping_address;
      if (addr && addr.province_code) {
        return { state: addr.province_code, postal: addr.zip || '' };
      }
    } catch (e) {}
    return null;
  }

  async function detectFromIpapi() {
    if (isFailCooled()) return null;
    try {
      const r = await fetch('https://ipapi.co/json/', { mode: 'cors' });
      if (!r.ok) throw new Error('ipapi http ' + r.status);
      const d = await r.json();
      if (d.country !== 'US' && d.country_code !== 'US') return { state: null, country: d.country };
      const state = d.region_code || '';
      const postal = d.postal || '';
      if (state) {
        clearFail();
        return { state: state, postal: postal };
      }
    } catch (e) {
      recordFail();
    }
    return null;
  }

  async function resolve() {
    if (isBot()) {
      dispatch(FALLBACK_ZONE, 'bot');
      return;
    }

    /* 1. Cart shipping address */
    const cart = await detectFromCart();
    if (cart && cart.state) {
      saveCache(cart.state, cart.postal);
      dispatch(resolveZoneKey(cart.state, cart.postal), 'cart', { state: cart.state, postal: cart.postal });
      return;
    }

    /* 2. localStorage cache */
    const cached = loadCache();
    if (cached && cached.state) {
      dispatch(resolveZoneKey(cached.state, cached.postal), 'cache', { state: cached.state, postal: cached.postal });
      return;
    }

    /* 3. Shopify.country */
    if (window.Shopify && Shopify.country && Shopify.country !== 'US') {
      dispatch(FALLBACK_ZONE, 'non-us', { state: null });
      return;
    }

    /* 4. ipapi.co */
    const ip = await detectFromIpapi();
    if (ip && ip.state) {
      saveCache(ip.state, ip.postal);
      dispatch(resolveZoneKey(ip.state, ip.postal), 'ipapi', { state: ip.state, postal: ip.postal });
      return;
    }

    /* 5. Fallback */
    dispatch(FALLBACK_ZONE, 'fallback');
  }

  resolve();
})();
